import { odinKeyFor, hasNonCippPlatform } from './customerKey';

/**
 * The tenant picker's list for users without Platforms (spec 2026-10-06 §11, Ruling H).
 *
 * CIPP-MCP `list_tenants` is Platforms-only at the gateway, so everyone else builds the
 * picker from the Odin-MCP `list_customers` rows that fetchCustomerMappings() returns
 * (Odin-MCP src/types/unifiedReport.ts CustomerMapping):
 *   - `platformIds.cipp`: the CIPP / Entra tenant GUID, which becomes the tenant `id`
 *     (the same id the CIPP list uses, so mapping lookups by tenant id keep working);
 *   - `displayName`: the customer name, which becomes `name`;
 *   - `id`: Odin-MCP's own customer id; it is NOT the tenant id, so the row is spread first
 *     and the tenant `id` written after it.
 * Rows without a CIPP tenant id are customers whose M365 CSG does not manage (spec
 * 2026-10-19): they are listed under an `odin:` key by nonM365TenantsFromMappings().
 *
 * @param {Array<object>} rows - customer mappings from fetchCustomerMappings()
 * @returns {Array<{id: string, name: string, domain: null, m365Managed: boolean}>}
 */
export function tenantsFromMappings(rows) {
  if (!Array.isArray(rows)) return [];
  const managed = rows
    .filter((row) => row && row.platformIds && row.platformIds.cipp)
    .map((row) => ({
      ...row,
      id: row.platformIds.cipp,
      name: row.displayName || row.name || 'Unknown Tenant',
      domain: null,
      m365Managed: true,
    }));
  return [...managed, ...nonM365TenantsFromMappings(rows)];
}

/**
 * Picker entries for mappings without a CIPP tenant id. A row needs an Odin id and at least
 * one other platform id, so an empty or abandoned mapping never reaches the picker.
 *
 * @param {Array<object>} rows - customer mappings from fetchCustomerMappings()
 * @returns {Array<object>}
 */
export function nonM365TenantsFromMappings(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row && row.id && !row.platformIds?.cipp && hasNonCippPlatform(row.platformIds))
    .map((row) => {
      const name = row.displayName || row.name || 'Unknown Tenant';
      return { ...row, id: odinKeyFor(row), name, displayName: name, domain: null, m365Managed: false };
    });
}

/**
 * The picker list for Platforms users: the CIPP tenants, plus the non-M365 customers when the
 * mappings loaded. If they failed, the list is just the CIPP tenants.
 *
 * @param {Array<object>} cippTenants - normalised CIPP `list_tenants` rows
 * @param {PromiseSettledResult} mappingsResult - the settled fetchCustomerMappings() call
 * @returns {Array<object>}
 */
export function mergePickerTenants(cippTenants, mappingsResult) {
  if (mappingsResult?.status !== 'fulfilled' || !Array.isArray(mappingsResult.value)) return cippTenants;
  return [...cippTenants, ...nonM365TenantsFromMappings(mappingsResult.value)];
}
